import ShareIcon from "@mui/icons-material/Share";
import * as React from "react";
import { useHref, useParams } from "react-router-dom";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import {
  RobotoIconButton,
  RobotoTooltip,
  ShareWorkspaceModal,
} from "@/shared/components";
import { enrichVizStateWithEphemeralState } from "@/shared/domain/workspaces/enrichVizStateWithEphemeralState";
import { WorkspaceRecord } from "@/shared/domain/workspaces/WorkspaceRecord";
import { SearchParams } from "@/shared/environment";
import { usePopupState } from "@/shared/hooks";
import { Goto } from "@/shared/navigation/Goto";
import { ErrorMonitoringService } from "@/shared/services";
import { useVizState } from "@/shared/state/visualization";
import { nanoSecToSecStr } from "@/shared/time";

import { useEphemeralPanelState, useWorkspaceTimer } from "../WorkspaceCtx";

import { createSharedWorkspace } from "./createSharedWorkspace";

/**
 * Update the browser URL to include the workspace ID and current timestamp
 * so that refreshing the page loads the shared workspace.
 *
 * @param resolvedHref - The basename-resolved path for the visualizer route
 *   (from `useHref`), which correctly handles dev stack basename prefixes.
 * @param currentTime - The current workspace timestamp.
 */
function updateBrowserUrl(resolvedHref: string, currentTime: bigint) {
  const url = new URL(resolvedHref, window.location.origin);

  // Set only the timestamp as the query string
  url.search = new URLSearchParams({
    [SearchParams.TIMESTAMP]: nanoSecToSecStr(currentTime),
  }).toString();

  window.history.replaceState(null, "", url.toString());
}

export function ShareWorkspaceButton() {
  const state = useVizState();
  const { workspaceService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const [ephemeralPanelState] = useEphemeralPanelState();
  const { workspaceId: urlWorkspaceId } = useParams();
  const timer = useWorkspaceTimer();

  const workspacePopup = usePopupState();
  const [record, setRecord] = React.useState<WorkspaceRecord | null>(null);
  const [createWorkspaceError, setCreateWorkspaceError] =
    React.useState<Error | null>(null);
  const isSharingRef = React.useRef(false);

  // Track the workspace ID that's currently in the browser URL.
  // useParams() doesn't update after window.history.replaceState, so we
  // maintain a ref that stays in sync with the actual URL.
  const effectiveWorkspaceIdRef = React.useRef<string | undefined>(
    urlWorkspaceId,
  );

  // Keep the ref in sync if React Router navigates to a different workspace
  React.useEffect(() => {
    effectiveWorkspaceIdRef.current = urlWorkspaceId;
  }, [urlWorkspaceId]);

  // Resolve the base visualizer href (includes basename for dev stacks)
  // at the hook level so it can be used inside the async callback below.
  const visualizerBaseHref = useHref(Goto.route.Visualize);

  const handleShareWorkspace = () => {
    if (isSharingRef.current) {
      return;
    }
    isSharingRef.current = true;

    const orgId = currentOrganization?.org_id || "";

    const enrichedVizState = enrichVizStateWithEphemeralState(
      state,
      ephemeralPanelState,
    );

    const existingWorkspaceId = effectiveWorkspaceIdRef.current;

    createSharedWorkspace(
      workspaceService,
      enrichedVizState,
      orgId,
      existingWorkspaceId,
    )
      .then((newRecord) => {
        setRecord(newRecord);
        setCreateWorkspaceError(null);
        const resolvedHref = `${visualizerBaseHref}/${newRecord.workspace_id}`;
        updateBrowserUrl(resolvedHref, timer.currentTime);
        // Keep the ref in sync with the URL we just wrote
        effectiveWorkspaceIdRef.current = newRecord.workspace_id;
      })
      .catch((e: Error) => {
        ErrorMonitoringService.captureError(e);
        setCreateWorkspaceError(e);
        setRecord(null);
      })
      .finally(() => {
        isSharingRef.current = false;
        workspacePopup.open();
      });
  };

  return (
    <>
      <RobotoTooltip title="Share workspace">
        <span>
          <RobotoIconButton
            eventName={"ShareWorkspaceClicked"}
            eventProperties={{}}
            onClick={handleShareWorkspace}
            sx={{ padding: "0.5rem" }}
            size="small"
          >
            <ShareIcon
              style={{
                width: "1.25rem",
                height: "1.25rem",
              }}
            />
          </RobotoIconButton>
        </span>
      </RobotoTooltip>

      <ShareWorkspaceModal
        open={workspacePopup.isOpen}
        record={record}
        handleClose={workspacePopup.close}
        error={createWorkspaceError}
      />
    </>
  );
}
